import { useEffect, useState } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import SpecialModal from '../../components/SpecialModal.tsx';
import { type Special, type SpecialSchedule } from '../../types';
import { useSpecials } from '../../hooks/useSpecials.ts';
import {
  BUSINESS_TIME_ZONE,
  businessDatetimeLocalToISO,
  formatDateTimeInBusinessTZ,
} from '../../services/specialsService.ts';

export default function SpecialsPage() {
  const {
    specials,
    specialSchedules,
    loading,
    error,
    fetchSpecials,
    saveSpecial,
    deleteSpecial,
    saveSchedule,
    deleteSchedules,
  } = useSpecials();

  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Special | null>(null);

  useEffect(() => {
    void fetchSpecials();
  }, [fetchSpecials]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  const openModal = (special?: Special) => {
    setEditing(special ?? null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (data: {
    title: string;
    imageUrl: string;
    linkType: Special['link_type'];
    linkTarget: string | null;
    homepage: boolean;
    bannerNumber: number;
    expiresAt: string;
    specialMode: 'one_time' | 'forever' | 'recurring';
    schedule: SpecialSchedule;
  }) => {
    if (!data.title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!data.imageUrl.trim()) {
      alert('Please select or upload an image');
      return;
    }

    if (data.linkType === 'url') {
      if (!data.linkTarget?.trim()) {
        alert('Please enter a URL');
        return;
      }
    } else if (!data.linkTarget) {
      alert('Please select a link target');
      return;
    }

    if (data.specialMode === 'recurring') {
      if (data.schedule.recurrence_type === 'weekly' && data.schedule.days_of_week.length === 0) {
        alert('Please choose at least one day of the week');
        return;
      }

      if (data.schedule.recurrence_type === 'monthly' && !data.schedule.day_of_month) {
        alert('Please choose a day of the month');
        return;
      }

      if (data.schedule.ends_at <= data.schedule.starts_at) {
        alert('Schedule end time must be after start time');
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        title: data.title.trim(),
        image_url: data.imageUrl,
        link_type: data.linkType,
        link_target: data.linkTarget,
        homepage: data.homepage,
        banner_number: data.homepage ? data.bannerNumber : null,
        expires_at:
            data.specialMode === 'one_time' && data.expiresAt
                ? businessDatetimeLocalToISO(data.expiresAt)
                : null,
      };

      const specialId = await saveSpecial(payload as Parameters<typeof saveSpecial>[0], editing?.id);

      if (data.specialMode === 'recurring') {
        const schedulePayload = {
          special_id: specialId,
          recurrence_type: data.schedule.recurrence_type,
          interval_count: data.schedule.interval_count,
          starts_at: data.schedule.starts_at,
          ends_at: data.schedule.ends_at,
          timezone: data.schedule.timezone || BUSINESS_TIME_ZONE,
          start_date: data.schedule.start_date || null,
          end_date: data.schedule.end_date || null,
          days_of_week: data.schedule.recurrence_type === 'weekly' ? data.schedule.days_of_week : null,
          day_of_month: data.schedule.recurrence_type === 'monthly' ? data.schedule.day_of_month : null,
        };

        await saveSchedule(schedulePayload, specialId);
      } else {
        await deleteSchedules(specialId);
      }

      closeModal();
      await fetchSpecials();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error saving special');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (special: Special) => {
    if (!confirm(`Delete "${special.title}"?`)) return;

    try {
      await deleteSpecial(special.id);
      await fetchSpecials();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deleting special');
    }
  };

  const getSpecialBadgeInfo = (special: Special, schedule?: SpecialSchedule | null) => {
    const now = new Date();

    const parseTime = (time: string, base = new Date()) => {
      const [h, m] = time.split(':').map(Number);
      const d = new Date(base);
      d.setHours(h || 0, m || 0, 0, 0);
      return d;
    };

    const formatScheduleName = (s: SpecialSchedule) => {
      if (s.recurrence_type === 'weekly') {
        const names: Record<number, string> = {
          1: 'Mon',
          2: 'Tue',
          3: 'Wed',
          4: 'Thu',
          5: 'Fri',
          6: 'Sat',
          7: 'Sun',
        };
        const days = (s.days_of_week ?? []).map((d) => names[d]).filter(Boolean);
        return days.length > 0 ? `Weekly · ${days.join(', ')}` : 'Weekly';
      }

      if (s.recurrence_type === 'monthly') {
        return s.day_of_month ? `Monthly · Day ${s.day_of_month}` : 'Monthly';
      }

      return 'Daily';
    };

    const isPastEnd = (s: SpecialSchedule) => {
      if (!s.end_date) return false;

      const today = new Date(now);
      today.setHours(0, 0, 0, 0);

      const end = new Date(s.end_date);
      end.setHours(0, 0, 0, 0);

      if (today > end) return true;
      if (today < end) return false;

      return now > parseTime(s.ends_at, now);
    };

    const isBeforeStart = (s: SpecialSchedule) => {
      if (!s.start_date) return false;

      const today = new Date(now);
      today.setHours(0, 0, 0, 0);

      const start = new Date(s.start_date);
      start.setHours(0, 0, 0, 0);

      return today < start;
    };

    const matchesDay = (s: SpecialSchedule) => {
      const dow = now.getDay();
      const mapped = dow === 0 ? 7 : dow;

      if (s.recurrence_type === 'weekly') return (s.days_of_week ?? []).includes(mapped);
      if (s.recurrence_type === 'monthly') return s.day_of_month === now.getDate();

      return true;
    };

    if (!schedule) {
      if (!special.expires_at) {
        return {
          label: 'Never ends',
          name: 'One-time',
          className: 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
        };
      }

      return {
        label: new Date(special.expires_at) > now ? 'Active' : 'Expired',
        name: 'One-time',
        className:
            new Date(special.expires_at) > now
                ? 'bg-blue-50 text-blue-700 ring-blue-200'
                : 'bg-stone-100 text-stone-600 ring-stone-200',
      };
    }

    if (!schedule.active) {
      return {
        label: 'Inactive',
        name: formatScheduleName(schedule),
        className: 'bg-stone-100 text-stone-600 ring-stone-200',
      };
    }

    if (isBeforeStart(schedule) || isPastEnd(schedule)) {
      return {
        label: 'Ended',
        name: formatScheduleName(schedule),
        className: 'bg-amber-50 text-amber-700 ring-amber-200',
      };
    }

    const cur = parseTime(now.toTimeString().slice(0, 5), now);
    const start = parseTime(schedule.starts_at, now);
    const end = parseTime(schedule.ends_at, now);

    if (matchesDay(schedule) && cur >= start && cur <= end) {
      return {
        label: 'Active',
        name: formatScheduleName(schedule),
        className: 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
      };
    }

    return {
      label: 'Upcoming',
      name: formatScheduleName(schedule),
      className: 'bg-blue-50 text-blue-700 ring-blue-200',
    };
  };

  return (
      <div>
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-stone-900">Specials</h2>
            <p className="mt-1 text-sm text-stone-500">Manage promotional content and featured items</p>
          </div>
          <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 rounded-lg bg-[#ee2b74] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#d4245f] transition"
          >
            <PlusIcon className="h-4 w-4" /> Add Special
          </button>
        </div>

        {/* List */}
        {loading ? (
            <div className="py-12 text-center text-sm text-stone-400">Loading specials…</div>
        ) : specials.length === 0 ? (
            <div className="rounded-2xl border border-stone-200 bg-white px-6 py-12 text-center shadow-sm">
              <p className="text-stone-400">No specials found</p>
            </div>
        ) : (
            <div className="space-y-3">
              {specials.map((special) => {
                const schedule = specialSchedules.find((s) => s.special_id === special.id && s.active);
                const badgeInfo = getSpecialBadgeInfo(special, schedule);

                return (
                    <div
                        key={special.id}
                        className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-4 shadow-sm transition-colors hover:bg-stone-50/50"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-4">
                        {special.image_url ? (
                            <img
                                src={special.image_url}
                                alt={special.title}
                                className="h-14 w-14 flex-shrink-0 rounded-lg border border-stone-200 object-cover"
                            />
                        ) : (
                            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-stone-200 bg-stone-100">
                              <span className="text-xs text-stone-400">—</span>
                            </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-stone-900">{special.title}</p>
                          <p className="text-sm text-stone-500">
                            <span className="font-mono text-xs uppercase">{special.link_type}</span>
                            {' → '}
                            {special.link_target}
                          </p>

                          <div className="mt-2 flex flex-wrap gap-2">
                            {special.homepage && (
                                <span className="inline-block rounded-full bg-[#f0f7e6] px-2.5 py-1 text-xs font-medium text-[#5a7f28] ring-1 ring-[#c5e08a]">
                          Homepage
                        </span>
                            )}

                            {special.homepage && special.banner_number && (
                                <span className="inline-block rounded-full bg-pink-50 px-2.5 py-1 text-xs font-medium text-[#ee2b74] ring-1 ring-pink-200">
                          Banner {special.banner_number}
                        </span>
                            )}

                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${badgeInfo.className}`}
                            >
                        <span>{badgeInfo.label}</span>
                        <span className="text-[10px] opacity-70">·</span>
                        <span>{badgeInfo.name}</span>
                      </span>

                            {special.expires_at && !schedule && (
                                <span className="inline-block rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                          Ends {formatDateTimeInBusinessTZ(special.expires_at)} SAST
                        </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="ml-4 flex items-center gap-2">
                        <button
                            onClick={() => openModal(special)}
                            className="rounded-lg border border-stone-200 p-2 text-stone-600 transition hover:bg-stone-50 hover:text-stone-900"
                            title="Edit"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>

                        <button
                            onClick={() => void handleDelete(special)}
                            className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50"
                            title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                );
              })}
            </div>
        )}

        <SpecialModal
            isOpen={isModalOpen}
            editing={editing}
            existingSchedules={specialSchedules}
            saving={saving}
            onClose={closeModal}
            onSubmit={handleSubmit}
        />
      </div>
  );
}